import { List, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { ValueDisplayCard } from "../../components/NumericMetricCard.component";
import { StatsQuery } from "../../extensions/sw/modules/stats/types";
import { statsWorkerQuery } from "../../extensions/sw/modules/stats/utils";
import {
  useEnigmaAvgErrors,
  useEnigmaAvgHints,
  useEnigmaAvgTime,
  useEnigmaBugsRate,
  useEnigmaDropRate,
  useEnigmaFailureRate,
  useEnigmaNoHintsProvidedRate,
  useEnigmaSuccessRate,
  useEnigmaTotalStartsCount,
} from "../../hooks/enigmaStats.hooks";
import { EnigmaMetrics } from "../../libs/gameAnalysis/enigma";
import { MatchFilter } from "../../libs/statsFilters";
import { formatSecondsToDDHHMMSS, isToday } from "../../libs/utils";
import { useChallengeMatchOverviewDialog } from "../../providers/ChallengeMatchOverviewDialogProvider";

export type EnigmaMetricsOverviewProps = {
  enigma?: EnigmaMetrics;
};

export function EnigmaOverview({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  return (
    <Stack direction={"column"} spacing={2}>
      <Typography variant="h6" gutterBottom>
        Panoramica
      </Typography>
      <Stack direction="row" spacing={2}>
        <EnigmaTotalStartsCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaTotalSuccessesCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaTotalResignationsCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaTotalDropsCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaTotalBugsCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaTotalNoHintsProvidedCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <EnigmaAverageTimeCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaAverageErrorsCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
        <EnigmaAverageHintsCard
          cityEscapeId={cityEscapeId}
          enigmaId={enigmaId}
          filters={filters}
        />
      </Stack>
    </Stack>
  );
}

export function EnigmaTotalStartsCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const dialog = useChallengeMatchOverviewDialog("EnigmaTotalStartsCard");
  const allMatches = useEnigmaTotalStartsCount(cityEscapeId, enigmaId, filters);

  return (
    <ValueDisplayCard
      title="Avvii totali"
      isLoading={allMatches.isLoading}
      value={allMatches.data ?? 0}
      error={allMatches.error}
      contextMenuItems={[
        {
          label: "Vedi informazioni partite",
          onClick: async () => {
            dialog.openModal({ title: "Avvii totali", loading: true });
            try {
              const result = await statsWorkerQuery(
                StatsQuery.ALL_MATCHES_ENIGMAS,
                { cityEscapeIds: cityEscapeId, enigmaId, filters }
              );
              dialog.setData(result);
            } catch (error) {
              console.error("Error fetching all matches:", error);
              dialog.setLoading(false);
            }
          },
        },
      ]}
    />
  );
}

export function EnigmaTotalSuccessesCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const dialog = useChallengeMatchOverviewDialog("EnigmaTotalSuccessesCard");
  const successRate = useEnigmaSuccessRate(cityEscapeId, enigmaId, filters);

  return (
    <ValueDisplayCard
      title="Successi"
      value={`${successRate.data?.toFixed(1) ?? 0}%`}
      isLoading={successRate.isLoading}
      error={successRate.error}
      contextMenuItems={[
        {
          label: "Vedi informazioni partite",
          onClick: async () => {
            console.debug("Clicked view successful matches");
            dialog.openModal({
              loading: true,
              title: "Successi totali",
              highlightColumns: ["challenge_solution"],
            });
            try {
              const result = await statsWorkerQuery(
                StatsQuery.SUCCESSFUL_MATCHES,
                { cityEscapeIds: cityEscapeId, enigmaId, filters }
              );
              console.debug(
                "Fetched successful matches opening dialog",
                result
              );
              dialog.setData(result);
            } catch (error) {
              console.error("Error fetching successful matches:", error);
              dialog.setLoading(false);
            }
          },
        },
      ]}
    />
  );
}

export function EnigmaTotalDropsCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const dialog = useChallengeMatchOverviewDialog("EnigmaTotalDropsCard");
  const drops = useEnigmaDropRate(cityEscapeId, enigmaId, filters);

  return (
    <ValueDisplayCard
      title="Drop"
      value={`${drops.data?.toFixed(1) ?? 0}%`}
      isLoading={drops.isLoading}
      error={drops.error}
      contextMenuItems={[
        {
          label: "Vedi informazioni partite",
          onClick: async () => {
            dialog.openModal({
              loading: true,
              title: "Drop",
            });
            try {
              const result = await statsWorkerQuery(
                StatsQuery.DROPPING_MATCHES,
                { cityEscapeIds: cityEscapeId, enigmaId, filters }
              );
              dialog.setData(result);
            } catch (error) {
              console.error("Error fetching drop matches:", error);
              dialog.setLoading(false);
            }
          },
        },
      ]}
    />
  );
}

export function EnigmaTotalBugsCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const dialog = useChallengeMatchOverviewDialog("EnigmaTotalBugsCard");
  const bugs = useEnigmaBugsRate(cityEscapeId, enigmaId, filters);
  return (
    <ValueDisplayCard
      title="Bugs"
      // value={`${drops.data?.toFixed(1) ?? 0}%`}
      value={`${bugs.data?.toFixed(1) ?? 0}%`}
      isLoading={bugs.isLoading}
      error={bugs.error}
      contextMenuItems={[
        {
          label: "Vedi informazioni partite",
          onClick: async () => {
            dialog.openModal({
              loading: true,
              title: "Bugs",
            });
            try {
              const result = await statsWorkerQuery(StatsQuery.BUGGED_MATCHES, {
                cityEscapeIds: cityEscapeId,
                enigmaId,
                filters,
              });
              dialog.setData(result);
            } catch (error) {
              console.error("Error fetching bugged matches:", error);
              dialog.setLoading(false);
            }
          },
        },
      ]}
    />
  );
}

export function EnigmaTotalResignationsCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const dialog = useChallengeMatchOverviewDialog("EnigmaTotalResignationsCard");
  const failureRate = useEnigmaFailureRate(cityEscapeId, enigmaId, filters);
  return (
    <ValueDisplayCard
      title="Rese"
      value={`${failureRate.data?.toFixed(1) ?? 0}%`}
      error={failureRate.error}
      isLoading={failureRate.isLoading}
      contextMenuItems={[
        {
          label: "Vedi informazioni partite",
          onClick: async () => {
            dialog.openModal({
              loading: true,
              title: "Rese",
              highlightColumns: ["challenge_requested_solution"],
            });
            try {
              const result = await statsWorkerQuery(
                StatsQuery.FAILING_MATCHES,
                { cityEscapeIds: cityEscapeId, enigmaId, filters }
              );
              dialog.setData(result);
            } catch (error) {
              console.error("Error fetching resignations:", error);
              dialog.setLoading(false);
            }
          },
        },
      ]}
    />
  );
}

export function EnigmaTotalNoHintsProvidedCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const dialog = useChallengeMatchOverviewDialog(
    "EnigmaTotalNoHintsProvidedCard"
  );
  const noHintsProvidedRate = useEnigmaNoHintsProvidedRate(
    cityEscapeId,
    enigmaId,
    filters
  );

  const formatPopover = ([startTime, endTime]: [number, number]) => {
    const start = new Date(startTime).toLocaleDateString();
    const end = isToday(endTime)
      ? "Oggi"
      : new Date(endTime).toLocaleDateString();

    return `${start} - ${end}`;
  };

  return (
    <ValueDisplayCard
      title="Senza ispirazioni fornite"
      value={`${noHintsProvidedRate.data?.toFixed(1) ?? 0}%`}
      isLoading={false}
      error={noHintsProvidedRate.error}
      contextMenuItems={[
        {
          label: "Vedi informazioni partite",
          onClick: () => {},
          // dialog.openModal(
          //   enigma?.aggregators?.hints?.matchesWithNoHintsProvided ?? [],
          //   "Senza ispirazioni fornite",
          //   ["challenge_hint", "challenge_requested_solution"]
          // ),
        },
      ]}
      popover={
        <Stack spacing={1}>
          <Typography variant="caption">Periodi senza ispirazioni</Typography>
          <List>
            {/* {enigma?.aggregators?.hints?.periodsWithNoHints.map(
              (period, index) => (
                <ListItem key={index}>
                  <ListItemText primary={formatPopover(period)} />
                </ListItem>
              )
            )} */}
          </List>
        </Stack>
      }
    />
  );
}

export function EnigmaAverageErrorsCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const averageErrors = useEnigmaAvgErrors(cityEscapeId, enigmaId, filters);
  return (
    <ValueDisplayCard
      title="Errori medi"
      value={averageErrors?.data?.toFixed(2) ?? 0}
      isLoading={averageErrors.isLoading}
      error={averageErrors.error}
    />
  );
}

export function EnigmaAverageHintsCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const averageHints = useEnigmaAvgHints(cityEscapeId, enigmaId, filters);

  return (
    <ValueDisplayCard
      title="Ispirazioni medie"
      value={averageHints?.data?.toFixed(2) ?? 0}
      isLoading={averageHints.isLoading}
      error={averageHints.error}
    />
  );
}

export function EnigmaAverageTimeCard({
  cityEscapeId,
  enigmaId,
  filters,
}: {
  cityEscapeId?: string | null;
  enigmaId?: string;
  filters?: MatchFilter;
}) {
  const avgTime = useEnigmaAvgTime(cityEscapeId, enigmaId, filters);
  const formattedTime = useMemo(() => {
    return formatSecondsToDDHHMMSS((avgTime.data ?? 0) / 1000);
  }, [avgTime.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio"
      value={formattedTime}
      isLoading={avgTime.isLoading}
      error={avgTime.error}
    />
  );
}
